import { useState, type FormEvent } from 'react'
import { Link } from 'react-router-dom'

import { AuthShell } from '@/components/AuthShell'
import { Alert, Button, Field, Input } from '@/components/ui'
import { errorMessage, loginWithOAuth, register, type OAuthProvider } from '@/lib/pb'

/** PocketBase's own minimum. Enforced here too so the error is immediate. */
const MIN_PASSWORD = 8

export function Register() {
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [confirm, setConfirm] = useState('')
  const [error, setError] = useState('')
  const [done, setDone] = useState(false)
  const [busy, setBusy] = useState(false)
  const [oauthBusy, setOauthBusy] = useState<OAuthProvider | null>(null)

  async function onSubmit(event: FormEvent) {
    event.preventDefault()
    if (password !== confirm) {
      setError('The two passwords do not match.')
      return
    }
    if (password.length < MIN_PASSWORD) {
      setError(`Use at least ${MIN_PASSWORD} characters.`)
      return
    }
    setBusy(true)
    setError('')
    try {
      await register(email, password)
      setDone(true)
    } catch (err) {
      setError(errorMessage(err))
    } finally {
      setBusy(false)
    }
  }

  function onOAuth(provider: OAuthProvider) {
    setError('')
    setOauthBusy(provider)
    loginWithOAuth(provider)
      .catch((err) => setError(errorMessage(err)))
      .finally(() => setOauthBusy(null))
  }

  const anyBusy = busy || oauthBusy !== null

  if (done) {
    return (
      <AuthShell
        title="Check your email"
        subtitle={`A confirmation link is on its way to ${email}.`}
        footer={
          <Link className="text-brand hover:underline" to="/login">
            Back to sign in
          </Link>
        }
      >
        <Alert tone="info">
          You need to confirm your address before you can sign in. If nothing arrives within a few
          minutes, check your spam folder.
        </Alert>
      </AuthShell>
    )
  }

  return (
    <AuthShell
      title="Create your account"
      subtitle="One place for every renewal date you would rather not miss."
      footer={
        <>
          Already registered?{' '}
          <Link className="text-brand hover:underline" to="/login">
            Sign in
          </Link>
          <span className="mt-2 block text-xs">
            By creating an account you agree to our{' '}
            <Link className="text-brand hover:underline" to="/privacy">
              privacy policy
            </Link>
            .
          </span>
        </>
      }
    >
      <div className="space-y-4">
        {error && <Alert>{error}</Alert>}

        <div className="space-y-2">
          <Button
            type="button"
            variant="secondary"
            className="w-full"
            loading={oauthBusy === 'google'}
            disabled={anyBusy}
            onClick={() => onOAuth('google')}
          >
            Continue with Google
          </Button>
          <Button
            type="button"
            variant="secondary"
            className="w-full"
            loading={oauthBusy === 'apple'}
            disabled={anyBusy}
            onClick={() => onOAuth('apple')}
          >
            Continue with Apple
          </Button>
        </div>

        <div className="flex items-center gap-3 text-xs text-ink-muted">
          <span className="h-px flex-1 bg-line" aria-hidden="true" />
          or with email
          <span className="h-px flex-1 bg-line" aria-hidden="true" />
        </div>

        <form onSubmit={onSubmit} className="space-y-4">
          <Field label="Email" htmlFor="email">
            <Input
              id="email"
              type="email"
              autoComplete="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </Field>

          <Field label="Password" htmlFor="password" hint={`At least ${MIN_PASSWORD} characters.`}>
            <Input
              id="password"
              type="password"
              autoComplete="new-password"
              required
              minLength={MIN_PASSWORD}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
          </Field>

          <Field label="Confirm password" htmlFor="confirm">
            <Input
              id="confirm"
              type="password"
              autoComplete="new-password"
              required
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
            />
          </Field>

          <Button type="submit" loading={busy} disabled={anyBusy} className="w-full">
            Create account
          </Button>
        </form>
      </div>
    </AuthShell>
  )
}
